import { useRouter } from 'next/router'

import appConfig from '~/config/app'
import { Link } from '~/components/Link'
import ChecklistNotification from '~/components/Modals/GamificationModal/components/ChecklistNotification'
import EndOfLeagueNotification from '~/components/Modals/GamificationModal/components/EndOfLeagueNotification'
import JoinLeaderboardNotification from '~/components/Modals/GamificationModal/components/JoinLeaderboardNotification'
import { setIsNotificationListOpen } from '~/gamification/slices/gamification.slice'
import { useSetMarkAsReadMutation } from '~/redux/apis/notification-api'
import { useAppDispatch } from '~/redux/hooks'
import { setOpenChecklist } from '~/redux/slices/dashboard'
import ROUTES from '~/shared/constants/routes.const'
import GlobalLeaderboardNotification from '~/shared/layouts/components/HeaderTop/components/GlobalLeaderboardNotification'
import { NotificationData, NotificationType } from '~/shared/types/notification.types'
import NotificationAnalytics from '~/analytics/notification/notificationAnalytics'
import TeacherAccessNotification from '~/shared/layouts/components/HeaderTop/components/TeacherAccessNotification'
import { useGetTimePast } from '~/shared/hooks/useGetTimePast'

export default function LinkNotifications({
  data,
  parentClassNames,
}: {
  data: NotificationData
  parentClassNames: string
}): React.ReactElement {
  const dispatch = useAppDispatch()
  const [setMarkAsRead] = useSetMarkAsReadMutation()
  const router = useRouter()
  const time = useGetTimePast(data.createdAt)

  const teacherBlogUrl =
    router.locale === 'no'
      ? '/tutor-on-demand/blog/gratiskonto-for-laerere-med-ubegrenset-tilgang-til-materiell'
      : router.locale === 'uk'
      ? '/tutor-on-demand/blog/free-access-for-teachers'
      : '/tutor-on-demand/blog/free-access-for-world-teachers'

  const logInfo = (): Record<string, string | number> | undefined => {
    switch (data.message.title) {
      case NotificationType.LEADERBOARD_JOIN:
        return { league: data.message.league as string }
      case NotificationType.LEADERBOARD_SEASON_END_RESULT:
        return { new_league: data.message.new_league as number }
      case NotificationType.CHECKLIST:
        return { item_name: data.message.item_name }
      case NotificationType.TEACHER_ACCESS:
        return { title: data.message.title }
      default:
        return undefined
    }
  }

  const routeTo = (): string => {
    switch (data.message.title) {
      case NotificationType.LEADERBOARD_JOIN:
        return ROUTES.LEADERBOARD
      case NotificationType.LEADERBOARD_SEASON_END_RESULT:
        return ROUTES.LEADERBOARD
      case NotificationType.LEADERBOARD_LAST_WEEK_RESULT:
        return ROUTES.LEADERBOARD
      case NotificationType.CHECKLIST:
        return ROUTES.DASHBOARD
      case NotificationType.TEACHER_ACCESS:
        // The teacher-access article lives under /tutor-on-demand/blog, which 404s in the school platform.
        return appConfig.isSchoolPlatform ? ROUTES.DASHBOARD : teacherBlogUrl
      default:
        return ROUTES.DASHBOARD
    }
  }

  const onClick = ({
    id,
    isRead,
    type,
    dataToLog,
  }: {
    id: number
    isRead: boolean
    type: NotificationType
    dataToLog?: Record<string, string | number>
  }): void => {
    NotificationAnalytics.logNotificationClick(type, dataToLog)
    if (!isRead) {
      setMarkAsRead({ notificationIds: [id] })
    }
    dispatch(setIsNotificationListOpen(false))
    if (type === NotificationType.CHECKLIST) {
      dispatch(setOpenChecklist(true))
    }
  }
  return (
    <Link
      className="relative flex w-full"
      key={data.id}
      onClick={() =>
        onClick({
          id: data.id,
          isRead: data.isRead,
          type: data.message.title,
          dataToLog: logInfo(),
        })
      }
      to={routeTo()}
    >
      <div className={parentClassNames}>
        {data.message.title === NotificationType.LEADERBOARD_JOIN && (
          <JoinLeaderboardNotification message={data.message} />
        )}
        {data.message.title === NotificationType.LEADERBOARD_SEASON_END_RESULT && (
          <EndOfLeagueNotification message={data.message} />
        )}
        {data.message.title === NotificationType.CHECKLIST && <ChecklistNotification message={data.message} />}
        {data.message.title === NotificationType.LEADERBOARD_LAST_WEEK_RESULT && (
          <GlobalLeaderboardNotification notificationData={data} />
        )}
        {data.message.title === NotificationType.TEACHER_ACCESS && <TeacherAccessNotification />}

        <span className="px-5 text-xs text-purple-light-top font-semibold">{time}</span>
      </div>
      {!data.isRead && (
        <div className="absolute flex justify-center items-center right-3 bottom-3">
          <span className="animate-ping absolute inline-flex rounded-full bg-purple-bright opacity-75 w-3.5 h-3.5" />
          <span className="absolute flex justify-center items-center rounded-full bg-purple w-2.5 h-2.5 text-xxs font-semibold" />
        </div>
      )}
    </Link>
  )
}
